import React, { useEffect, useRef, useState } from 'react';
import { emitCustomEvent } from 'react-custom-events';
import {
  useAtomValue as useSegmentValue,
  useAtom as useSegment,
  useSetAtom,
} from 'jotai';
import {
  getSessionDetails,
  validateFileProcess,
  markSessionContextAsRestored,
} from '../../../store/globalStore/actions/fileUploadActions';
import { fileUploadSegment } from '../../../store/globalStore/stateSegments/fileUploadSegment';
import { useSnackBar } from '../../../hooks/snackBar/useSnackBar';
import SnackBar, {
  SnackBarPosition,
  SnackBarVariant,
} from '../../atoms/SnackBar';
import { ANALYTICS } from '../../../constants/common';
import { notificationsAtom } from '../../../store/markettingContentGeneration/stateSegments/notificationsSegment';
import { useNotifications } from '../../../hooks/useNotifications';
import { fetchUserSessionContext } from '../../../store/markettingContentGeneration/actions/majorProcessActions/fetchUserSessionContext';
import { translate } from '../../../localization';
import {
  clearSessionRestoreLoading,
  setSessionRestoreLoading,
} from '../../../utils/commonUtils';
import { isReloggingAfterTimeoutSegment } from '../../../store/globalStore/stateSegments/sessionSegment';
import store from '../../../store/markettingContentGeneration';
import { trackSessionRecoveryStartNew } from '../../../services/Analytics';
import PageLoader from '../../molecules/PageLoader';
import styles from './BackgroundTaskManager.module.css';

const BackgroundTaskManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [fileUploadValue] = useSegment(fileUploadSegment);
  const initializer = useRef(false);
  const fileUploadValueRef = useRef(fileUploadValue);
  const setNotifications = useSetAtom(notificationsAtom); // set atom
  const { notifications } = useNotifications();
  const fileUploadData = useSegmentValue(fileUploadSegment);
  const isReloggingAfterTimeout = useSegmentValue(
    isReloggingAfterTimeoutSegment
  );

  const {
    fileUploading,
    fileUploaded,
    //  error, success
  } = useSegmentValue(fileUploadSegment);

  const {
    isSnackBarVisible,
    hideSnackBar,
    notificationText,
    snackBarTheme,
    showSnackBar,
  } = useSnackBar(0, false);

  useEffect(() => {
    if (loading) {
      setSessionRestoreLoading(true);
    } else {
      clearSessionRestoreLoading();
    }
  }, [loading]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent): string | void => {
      event.preventDefault();
      if (fileUploading) {
        const message =
          'Leaving this page will terminate your file uploading process. Are you sure?';

        event.returnValue = message; // Standard for most browsers
        return message; // For some older browsers
      }
      // Explicitly return undefined when there is no need to show a warning
      return undefined;
    };
    if (fileUploaded) {
      window.removeEventListener('beforeunload', () => undefined);
    }
    // Add event listener for beforeunload
    if (fileUploading) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [fileUploading, fileUploaded]);

  useEffect(() => {
    fileUploadValueRef.current = fileUploadValue;
  }, [fileUploadValue]);

  useEffect(() => {
    if (Array.isArray(notifications)) {
      setNotifications(notifications); // store in atom
    }
  }, [notifications]);

  const validateFileProcessCb = async (): Promise<void> => {
    window.uploadInterval = setInterval(() => {
      validateFileProcess();
    }, 5000);
  };

  useEffect(() => {
    // Initial session check
    if (!initializer.current) {
      initializer.current = true;
      setLoading(true);

      getSessionDetails()
        .then(
          async ({
            sessionId,
            restore,
            contextSessionId,
          }: {
            sessionId: string;
            restore: boolean;
            contextSessionId: string;
          }) => {
            if (sessionId) {
              const continuePolling = await validateFileProcess();
              if (continuePolling) validateFileProcessCb();
            }

            // Handle session restoration using cached data to avoid duplicate calls
            if (
              restore &&
              contextSessionId &&
              !fileUploadData.sessionContextRestored
            ) {
              try {
                await fetchUserSessionContext({ sessionId: contextSessionId });
                markSessionContextAsRestored();

                // Only show the restore snackbar if user is relogging after session timeout
                setTimeout(() => {
                  if (isReloggingAfterTimeout) {
                    showSnackBar(
                      translate('session_restore_snackbar_message'),
                      SnackBarVariant.INFO
                    );
                    // Clear the flag after showing the snackbar
                    store.set(isReloggingAfterTimeoutSegment, false);
                  }
                }, 5000);
              } catch (err) {
                // eslint-disable-next-line
                console.error('Session restore error:', err);
                // Clear the relogging flag in case of error
                if (isReloggingAfterTimeout) {
                  store.set(isReloggingAfterTimeoutSegment, false);
                }
              }
            } else if (isReloggingAfterTimeout) {
              // If there's no session to restore but user was relogging after timeout, clear the flag
              store.set(isReloggingAfterTimeoutSegment, false);
            }

            setLoading(false);
          }
        )
        .catch(err => {
          // eslint-disable-next-line
          console.error(err);
          setLoading(false);
          // Clear the relogging flag in case of error
          if (isReloggingAfterTimeout) {
            store.set(isReloggingAfterTimeoutSegment, false);
          }
        });
    }

    return () => {
      if (window.uploadInterval) {
        clearInterval(window.uploadInterval);
        window.uploadInterval = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className={styles.page_loader_container}>
        <PageLoader />
      </div>
    );
  }

  if (isSnackBarVisible) {
    return (
      <SnackBar
        theme={snackBarTheme}
        notificationText={notificationText}
        hideSnackBar={hideSnackBar}
        position={SnackBarPosition.BOTTOM_CENTER}
        showCTAButton
        ctaLabel={translate('common_continue')}
        ctaCallback={() => {
          trackSessionRecoveryStartNew(
            ANALYTICS.SESSION_RECOVERY_FORM_NAME,
            'No'
          );
          hideSnackBar();
        }}
        showSecondaryButton
        secondaryButtonLabel={translate('common_start_fresh')}
        secondaryButtonCallback={() => {
          trackSessionRecoveryStartNew(
            ANALYTICS.SESSION_RECOVERY_FORM_NAME,
            'Yes'
          );
          emitCustomEvent('start-new-action');
          hideSnackBar();
        }}
      />
    );
  }

  return null;
};

export default BackgroundTaskManager;
